import React from 'react';
import { z } from 'zod';
import { T, useGT } from 'gt-react';
import {
  isSd844TemplateDateField,
  SD_844_TEMPLATE_FIELD_LABELS,
  sd844TemplateFieldGroup,
  sd844TemplateFieldSchema,
  type Sd844TemplateColumnMapping,
  type Sd844TemplateColumnSource,
  type Sd844TemplateField,
} from '@erp/rpc';
import { sortFieldOptions } from './sd844TemplateEditorState';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Badge } from 'src/components/ui/badge';
import { WarningIcon } from '@phosphor-icons/react';
import {
  SingleSelect,
  type SingleSelectOption,
} from 'src/components/common/SingleSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

/**
 * Binds each column of a supplier's uploaded claim form to one of our 844
 * fields.
 *
 * Split out of the template editor so the editor stays about the file (upload,
 * sheet, save, preview) and this stays about the bindings — the part with a row
 * per column and three source kinds.
 */

/** The dropdown's "leave this cell empty" option. */
const BLANK_VALUE = '__blank__';
/** The dropdown's "same text on every row" option. */
const CONSTANT_VALUE = '__constant__';

const FIELD_OPTIONS = sortFieldOptions(
  sd844TemplateFieldSchema.options.map((field) => ({
    value: field,
    label: SD_844_TEMPLATE_FIELD_LABELS[field],
    group: sd844TemplateFieldGroup(field),
  })),
);

const dateFormatSchema = z.enum(['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY']);
const DATE_FORMATS = dateFormatSchema.options;
type DateFormat = z.infer<typeof dateFormatSchema>;
type MappingStatus = 'resolved' | 'suggesting' | 'unresolved' | 'uncertain';

/** Sentinel for "write the date exactly as we store it". */
const RAW_DATE = 'raw';

function sourceToSelectValue(source: Sd844TemplateColumnSource): string {
  if (source.kind === 'blank') return BLANK_VALUE;
  if (source.kind === 'constant') return CONSTANT_VALUE;
  return source.field;
}

/**
 * Translate a dropdown selection back into a stored source.
 *
 * The field is PARSED, not cast: the Select can emit `''` when a selection is
 * cleared, and casting that through would build a source with an empty field
 * that only fails later, at save, against the contract schema. An unrecognised
 * value falls back to `blank`, which is the one source that is always valid.
 */
function selectValueToSource(value: string): Sd844TemplateColumnSource {
  if (value === CONSTANT_VALUE) return { kind: 'constant', value: '' };
  const field = sd844TemplateFieldSchema.safeParse(value);
  return field.success
    ? { kind: 'field', field: field.data }
    : { kind: 'blank' };
}

/** Parse a date-format selection; anything else means "as stored". */
function selectValueToDateFormat(value: string): DateFormat | undefined {
  const parsed = dateFormatSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

function mappingStatus({
  isSuggesting,
  isUnresolved,
  isUncertain,
}: {
  isSuggesting: boolean;
  isUnresolved: boolean;
  isUncertain: boolean;
}): MappingStatus {
  if (isSuggesting) return 'suggesting';
  if (isUncertain) return 'uncertain';
  if (isUnresolved) return 'unresolved';
  return 'resolved';
}

interface Props {
  mappings: Sd844TemplateColumnMapping[];
  onChangeSource: (
    columnIndex: number,
    source: Sd844TemplateColumnSource,
  ) => void;
  /** Columns the matcher could not place. */
  unresolved?: Set<number>;
  /** True while bindings are being proposed for a fresh upload. */
  isSuggesting?: boolean;
  /** Why the matcher proposed each binding. */
  reasons?: Map<number, string>;
  /** Columns matched with less than certainty. */
  uncertain?: Set<number>;
  /** columnIndex → what that column gets for one real sample line. */
  sampleValues?: Map<number, string | null>;
  /** True once a sample line has been loaded (so "empty" means empty). */
  hasSample?: boolean;
  readOnly?: boolean;
}

export function Sd844TemplateColumnMapper({
  mappings,
  onChangeSource,
  unresolved,
  isSuggesting = false,
  reasons,
  uncertain,
  sampleValues,
  hasSample = false,
  readOnly = false,
}: Props) {
  const gt = useGT();

  const groupLabels = {
    claim: gt('Claim (default 844 file)', {
      context: 'group of fields from the default 844 claim file',
    }),
    transaction: gt('Transaction (all 844D fields)', {
      context: 'group of fields from the full 844D transaction record',
    }),
    vdm: gt('VDM (debit memo)', {
      context: 'group of fields from the vendor debit memo',
    }),
  };

  /**
   * Options for the searchable dropdown. The two special entries come first and
   * auto-sort is off, so "leave blank" and "fixed text" stay at the top instead
   * of being filed under L and F among 38 field names.
   */
  // Grouped, so they render FIRST: the dropdown appends ungrouped options last.
  const specialGroup = gt('Not a field', {
    context: 'dropdown group for leave-blank and fixed-text options',
  });
  const options = [
    {
      id: BLANK_VALUE,
      label: gt('— Leave blank —', {
        context: 'dropdown option to leave a template column empty',
      }),
      group: specialGroup,
    },
    {
      id: CONSTANT_VALUE,
      label: gt('— Fixed text —', {
        context: 'dropdown option to write the same text in every row',
      }),
      group: specialGroup,
    },
    ...FIELD_OPTIONS.map((option) => ({
      id: option.value,
      label: option.label,
      group: groupLabels[option.group],
    })),
  ];

  return (
    <div className="space-y-2">
      <Label className="text-xs">
        <T context="heading for the list of template column bindings">
          Column mapping
        </T>
      </Label>
      <div className="space-y-1">
        <div className="text-muted-foreground flex items-center gap-2 text-[11px] font-medium uppercase">
          <span className="w-8 shrink-0 text-right">#</span>
          <span className="w-52 shrink-0">
            <T context="column heading: the supplier's column header">
              Supplier column
            </T>
          </span>
          <span className="w-36 shrink-0" />
          <span className="flex-1">
            <T context="column heading: where the value comes from">
              Filled from
            </T>
          </span>
          <span className="w-44 shrink-0">
            <T context="column heading: value from a real sample transaction">
              Sample value
            </T>
          </span>
        </div>
        {mappings.map((mapping) => (
          <MappingRow
            key={mapping.columnIndex}
            mapping={mapping}
            options={options}
            onChangeSource={onChangeSource}
            status={mappingStatus({
              isSuggesting,
              isUnresolved: unresolved?.has(mapping.columnIndex) ?? false,
              isUncertain: uncertain?.has(mapping.columnIndex) ?? false,
            })}
            reason={reasons?.get(mapping.columnIndex)}
            hasSample={hasSample}
            sampleValue={sampleValues?.get(mapping.columnIndex)}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

/** One supplier column: its header, where its value comes from, a sample. */
function MappingRow({
  mapping,
  options,
  onChangeSource,
  status,
  reason,
  hasSample,
  sampleValue,
  readOnly,
}: {
  mapping: Sd844TemplateColumnMapping;
  options: SingleSelectOption[];
  onChangeSource: Props['onChangeSource'];
  status: MappingStatus;
  reason: string | undefined;
  hasSample: boolean;
  sampleValue: string | null | undefined;
  readOnly: boolean;
}) {
  const gt = useGT();
  return (
    <div key={mapping.columnIndex} className="border-b py-1 last:border-0">
      {/* items-start + fixed line heights, NOT items-center: opening the
          dropdown briefly grows its wrapper, and centred text beside it would
          jump down. Everything is pinned to the button's 32px row instead. */}
      <div className="flex items-start gap-2">
        <span
          className="text-muted-foreground w-8 shrink-0 text-right text-xs leading-8"
          title={gt('Column {index}', {
            context: 'tooltip showing a spreadsheet column number',
            index: String(mapping.columnIndex),
          })}
        >
          {mapping.columnIndex}
        </span>
        <span
          className="w-52 shrink-0 truncate text-xs leading-8"
          title={mapping.headerLabel}
        >
          {mapping.headerLabel}
        </span>

        <ColumnStatus status={status} reason={reason} />

        {/* Fixed height: opening the dropdown mounts its popover beside the
            button, and SingleSelect's own spacing then grows its wrapper —
            which pushed this row (and the note line under it) down. */}
        <div className="h-8 min-w-0 flex-1">
          <SingleSelect
            options={options}
            shouldAutoSort={false}
            size="sm"
            fullWidth
            disabled={readOnly}
            searchPlaceholder={gt('Search fields', {
              context:
                'placeholder in the 844 template column mapping search box',
            })}
            value={
              options.find(
                (o) => o.id === sourceToSelectValue(mapping.source),
              ) ?? null
            }
            onChange={(selected) =>
              onChangeSource(
                mapping.columnIndex,
                selectValueToSource(selected?.id ?? BLANK_VALUE),
              )
            }
          />
        </div>

        {mapping.source.kind === 'constant' && (
          <Input
            className="h-8 w-32"
            value={mapping.source.value}
            disabled={readOnly}
            placeholder={gt('e.g. CAD', {
              context: 'placeholder for a fixed value written into every row',
            })}
            onChange={(e) =>
              onChangeSource(mapping.columnIndex, {
                kind: 'constant',
                value: e.target.value,
              })
            }
          />
        )}

        {mapping.source.kind === 'field' &&
          isSd844TemplateDateField(mapping.source.field) && (
            <DateFormatSelect
              field={mapping.source.field}
              dateFormat={mapping.source.dateFormat}
              disabled={readOnly}
              onChange={(source) => onChangeSource(mapping.columnIndex, source)}
            />
          )}

        <SampleValue hasSample={hasSample} value={sampleValue} />
      </div>

      {(mapping.hint || reason) && (
        <div className="text-muted-foreground ml-10 flex flex-wrap gap-x-4 text-[11px]">
          {mapping.hint && (
            <span>
              <T context="label for the mapping note written under a column header">
                Note:
              </T>{' '}
              <span className="text-foreground">{mapping.hint}</span>
            </span>
          )}
          {reason && <span>{reason}</span>}
        </div>
      )}
    </div>
  );
}

/** What this column would receive for the sample line. */
function SampleValue({
  hasSample,
  value,
}: {
  hasSample: boolean;
  value: string | null | undefined;
}) {
  if (!hasSample) return <span className="w-44 shrink-0" />;
  if (value === null || value === undefined || value === '') {
    return (
      <span className="text-muted-foreground w-44 shrink-0 truncate text-xs leading-8 italic">
        <T context="sample value cell when the column is left empty">(empty)</T>
      </span>
    );
  }
  return (
    <span
      className="w-44 shrink-0 truncate font-mono text-xs leading-8"
      title={value}
    >
      {value}
    </span>
  );
}

/** Display format for a date column, e.g. TE's `Ship Date (YYYY-MM-DD)`. */
function DateFormatSelect({
  field,
  dateFormat,
  disabled,
  onChange,
}: {
  field: Sd844TemplateField;
  dateFormat?: DateFormat;
  disabled?: boolean;
  onChange: (source: Sd844TemplateColumnSource) => void;
}) {
  const gt = useGT();

  return (
    <Select
      disabled={disabled}
      value={dateFormat ?? RAW_DATE}
      onValueChange={(value) => {
        const dateFormat = selectValueToDateFormat(value);
        onChange({
          kind: 'field',
          field,
          ...(dateFormat ? { dateFormat } : {}),
        });
      }}
    >
      <SelectTrigger className="h-8 w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={RAW_DATE}>
          {gt('As stored', {
            context: 'date format option leaving the stored value unchanged',
          })}
        </SelectItem>
        {DATE_FORMATS.map((format) => (
          <SelectItem key={format} value={format}>
            {format}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Says why a column is blank.
 *
 * An unplaceable column reads "couldn't determine" rather than sitting empty —
 * an empty dropdown looks like a deliberate choice, and this one is not.
 */
function ColumnStatus({
  status,
  reason,
}: {
  status: MappingStatus;
  reason: string | undefined;
}) {
  if (status === 'suggesting') {
    return (
      <span className="text-muted-foreground w-36 shrink-0 text-xs leading-8">
        <T context="shown while the 844 template column matcher is running">
          Matching…
        </T>
      </span>
    );
  }

  if (status === 'uncertain') {
    return (
      <Badge
        variant="outline"
        className="mt-1.5 w-36 shrink-0 justify-center gap-1 border-amber-400 text-xs text-amber-700 dark:text-amber-400"
        title={reason}
      >
        <WarningIcon className="size-3" />
        <T context="badge on an 844 template column whose automatic match is not certain">
          Check this match
        </T>
      </Badge>
    );
  }

  if (status !== 'unresolved') return <span className="w-36 shrink-0" />;

  return (
    <Badge
      variant="outline"
      className="mt-1.5 w-36 shrink-0 justify-center text-xs"
    >
      <T context="shown when the matcher could not identify a template column's source">
        Couldn&apos;t determine
      </T>
    </Badge>
  );
}
